/**
 * offsetCalculator.ts
 *
 * Pure, side-effect-free utility that implements the Cashflow Offset Logic
 * (חוקי קיזוז).
 *
 * ─── Business Rules ────────────────────────────────────────────────────────
 *
 * Law #2 – Category match
 *   A Known Action B offsets a Recurring Action A when both share the same
 *   `offset_code_id`.  The OffsetCode's `frequency_type` determines the
 *   comparison window (Law #3).
 *
 * Law #3 – Period match (based on ORIGINAL action_date, never the rolled date)
 *   | frequency_type       | Window                                       |
 *   |----------------------|----------------------------------------------|
 *   | monthly              | same calendar month  (YYYY-MM)               |
 *   | quarterly            | same calendar quarter (YYYY-Q)               |
 *   | semi_annually        | same half-year  (YYYY-H1 or YYYY-H2)         |
 *   | annually             | same calendar year   (YYYY)                  |
 *   | weekly / weekly_5/6  | same ISO week  (YYYY-Www)                    |
 *   | daily                | same calendar day    (YYYY-MM-DD)            |
 *   | custom               | same calendar month (fallback)               |
 *
 * ─── Calculation ───────────────────────────────────────────────────────────
 *   displayAmount = Math.max(0, recurringAmount − Σ matchedKnownAmounts)
 *   offsetAmount  = recurringAmount − displayAmount  (always ≥ 0)
 *
 * ─── Usage ─────────────────────────────────────────────────────────────────
 *   import { buildOffsetMap } from '@/lib/offsetCalculator';
 *
 *   const offsetMap = buildOffsetMap(recurringActions, knownActions, offsetCodes);
 *   const result    = offsetMap.get(recurringAction.id);
 *   //  result: { displayAmount, offsetAmount, rawAmount, matchedKnownIds }
 */

import type { RecurringActionLine } from '../types/database';
import type { KnownAction } from './knownActionsService';
import type { OffsetCode } from '../types/database';
import type { FrequencyType } from '../types/database';

// ─── Period-key helpers ─────────────────────────────────────────────────────

/** Returns the ISO week number (1-53) for a given Date. */
function isoWeek(d: Date): number {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  // Thursday in current week decides the year
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}

/**
 * Derives a string "period key" for a date given a FrequencyType.
 * Two dates that produce the same key are considered "in the same period".
 */
export function periodKey(dateStr: string, frequencyType: FrequencyType): string {
  // Parse as local date to avoid UTC-midnight drift issues
  const [year, month, day] = dateStr.split('-').map(Number) as [number, number, number];
  const d = new Date(year, (month ?? 1) - 1, day ?? 1);

  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const dd   = String(d.getDate()).padStart(2, '0');

  switch (frequencyType) {
    case 'daily':
      return `${yyyy}-${mm}-${dd}`;

    case 'weekly':
    case 'weekly_5':
    case 'weekly_6': {
      const ww = String(isoWeek(d)).padStart(2, '0');
      return `${yyyy}-W${ww}`;
    }

    case 'monthly':
      return `${yyyy}-${mm}`;

    case 'quarterly': {
      const q = Math.ceil(d.getMonth() / 3) + 1;   // months are 0-indexed
      return `${yyyy}-Q${q}`;
    }

    case 'semi_annually': {
      const half = d.getMonth() < 6 ? 'H1' : 'H2';
      return `${yyyy}-${half}`;
    }

    case 'annually':
      return `${yyyy}`;

    case 'custom':
    default:
      // Fall back to monthly grouping for custom / unknown types
      return `${yyyy}-${mm}`;
  }
}

// ─── Result type ─────────────────────────────────────────────────────────────

export interface OffsetResult {
  /** The raw (original) budget amount on the recurring action. */
  rawAmount: number;
  /** Sum of matched known-action amounts that offset this recurring action. */
  offsetAmount: number;
  /** What should be DISPLAYED: max(0, rawAmount − offsetAmount). */
  displayAmount: number;
  /** IDs of the KnownActions that contributed to the offset. */
  matchedKnownIds: string[];
  /** True when at least one known action matched (offset > 0). */
  isOffset: boolean;
}

// ─── Core calculation ─────────────────────────────────────────────────────────

/**
 * Calculate the offset result for a single recurring action against a
 * collection of known actions.
 *
 * @param recurring    The recurring action line being evaluated.
 * @param knownActions ALL known actions for the company (will be filtered here).
 * @param offsetCode   The OffsetCode attached to `recurring` (already resolved).
 *                     Pass `null` if the recurring action has no offset code.
 *
 * @returns OffsetResult — always defined (isOffset=false when no offset applies).
 */
export function calculateOffsetAmount(
  recurring:    RecurringActionLine,
  knownActions: KnownAction[],
  offsetCode:   OffsetCode | null,
): OffsetResult {
  const rawAmount = recurring.amount;

  // No offset code → no offset calculation
  if (!offsetCode || !recurring.offset_code_id) {
    return {
      rawAmount,
      offsetAmount:    0,
      displayAmount:   rawAmount,
      matchedKnownIds: [],
      isOffset:        false,
    };
  }

  const period = periodKey(recurring.action_date, offsetCode.frequency_type);

  // Filter known actions that:
  //   1. Share the same offset_code_id (Law #2)
  //   2. Are not ignored
  //   3. Fall within the same period as the recurring action (Law #3)
  const matched = knownActions.filter((ka) => {
    if (ka.offset_code_id !== recurring.offset_code_id) return false;
    if (ka.status === 'ignored') return false;
    return periodKey(ka.action_date, offsetCode.frequency_type) === period;
  });

  if (matched.length === 0) {
    return {
      rawAmount,
      offsetAmount:    0,
      displayAmount:   rawAmount,
      matchedKnownIds: [],
      isOffset:        false,
    };
  }

  // Sum uses Math.abs to handle both positive and negative amounts consistently
  const sumMatched = matched.reduce((acc, ka) => acc + Math.abs(ka.amount), 0);
  const offsetAmount  = Math.min(sumMatched, Math.abs(rawAmount)); // cannot offset more than budget
  const displayAmount = rawAmount >= 0
    ? Math.max(0, rawAmount - offsetAmount)
    : Math.min(0, rawAmount + offsetAmount); // for negative (expense) budgets

  return {
    rawAmount,
    offsetAmount,
    displayAmount,
    matchedKnownIds: matched.map((ka) => ka.id),
    isOffset:        offsetAmount > 0,
  };
}

// ─── Batch helper (used in component useMemo) ─────────────────────────────────

/**
 * Build a Map<recurringActionId, OffsetResult> for every recurring action in
 * one O(n·m) pass.  Pass the flat array of all offset codes so we can look up
 * each action's code without extra async calls.
 *
 * @param recurringActions All recurring action lines for the company.
 * @param knownActions     All known actions for the company.
 * @param offsetCodes      All offset codes for the company.
 */
export function buildOffsetMap(
  recurringActions: RecurringActionLine[],
  knownActions:     KnownAction[],
  offsetCodes:      OffsetCode[],
): Map<string, OffsetResult> {
  // Build a fast code-id → OffsetCode lookup
  const codeMap = new Map<string, OffsetCode>(offsetCodes.map((oc) => [oc.id, oc]));

  const result = new Map<string, OffsetResult>();

  for (const ra of recurringActions) {
    const offsetCode = ra.offset_code_id ? (codeMap.get(ra.offset_code_id) ?? null) : null;
    result.set(ra.id, calculateOffsetAmount(ra, knownActions, offsetCode));
  }

  return result;
}
